import { animate, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, inject, OnInit, Optional, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { CmsSearchBoxComponent, Price, RoutingService, WindowRef } from '@spartacus/core';
import { CmsComponentData, SearchBoxComponent, SearchBoxComponentService, SearchResults } from '@spartacus/storefront';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';

import { DpBaseSiteService } from '@app/core/services/base-site.service';
import { DpGtmService } from '@app/core/services/gtm.service';
import { DpProductsService } from '@app/core/services/products.service';
import { DpSearchBoxComponentService } from '@app/core/services/search-box-component.service';
import { DpSearchService } from '@app/core/services/search.service';
import { CustomerPrice } from '@app/core/types/backend/customer-price.type';
import { DpProduct } from '@app/core/types/view/dp-product.interface';

@Component({
    selector: 'dp-search-box',
    templateUrl: './search-box.component.html',
    styleUrls: ['./search-box.component.scss'],
    providers: [DpSearchBoxComponentService],
    animations: [
        trigger('inOutAnimation', [
            transition(':enter', [
                style({ height: 0, opacity: 0 }),
                animate('.2s ease-out', style({ height: 300, opacity: 1 }))
            ]),
            transition(':leave', [
                style({ height: 300, opacity: 1 }),
                animate('.2s ease-in', style({ height: 0, opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class DpSearchBoxComponent extends SearchBoxComponent implements OnInit {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  visible = false;
  focused = false;
  results$!: Observable<SearchResults>;
  readonly #baseSiteService = inject(DpBaseSiteService);

  constructor(
    protected searchBoxComponentService: SearchBoxComponentService,
    @Optional()
    protected componentData: CmsComponentData<CmsSearchBoxComponent>,
    protected winRef: WindowRef,
    protected routingService: RoutingService,
    private readonly dpSearchService: DpSearchService,
    private readonly dpGtmService: DpGtmService,
    private readonly dpProductsService: DpProductsService,
    private readonly router: Router
  ) {
    super(searchBoxComponentService, componentData, winRef, routingService);
    this.#baseSiteService
      .getActive()
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(() => {
        if (this.searchInput?.nativeElement?.value) {
          this.search(this.searchInput.nativeElement.value);
        }
      });
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.results$ = this.config$.pipe(switchMap((config) => this.searchBoxComponentService.getResults(config)));
  }

  hide(): void {
    this.visible = false;
  }

  isResultsListEmpty(results: SearchResults): boolean {
    return !!results.suggestions?.length || !!results.products?.length;
  }

  onSearchInputKeydownEnter($event: UIEvent): void {
    const value = this.searchInput?.nativeElement.value ?? '';

    this.dpSearchService.setTerm(value);

    this.close($event, true);
    // TODO: wait for search to complete before redirection
    this.launchSearchResult($event, value);
    this.updateChosenWord(value);
  }

  clickOnProduct(freeText: string, product: DpProduct, index: number): void {
    this.dispatchProductEvent({
      freeText,
      productCode: product.code ?? ''
    });

    this.dpProductsService
      .fetchPrice(product.code)
      .pipe(
        take(1),
        catchError((err) => of(err)),
        map((price) => (price as CustomerPrice).customerPrice ?? {})
      )
      .subscribe((dpPrice: Price) => {
        this.dpGtmService.setCategories(product.url, product.category, 'search_results');
        void this.router.navigateByUrl('p/' + product.code);
        void this.dpGtmService.pushTag('select_item', [
          {
            item_id: product.code,
            item_name: product.name,
            currency: dpPrice.currencyIso,
            index,
            price: dpPrice.value
          }
        ]);
      });
  }

  onFocus(): void {
    this.focused = true;
    this.open();
  }

  onBlur($event: UIEvent): void {
    this.blurSearchBox($event);
    this.close($event);
  }

  close(event: UIEvent, force?: boolean): void {
    super.close(event, force);

    this.hide();
  }

  protected blurSearchBox(event: UIEvent): void {
    super.blurSearchBox(event);
    this.blurNative();
  }

  private blurNative(): void {
    this.searchInput?.nativeElement.blur();
    this.focused = false;
  }
}
